import React, { useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import * as R from "./styles";
import Home from "../Pages/Home";
import NotFound from "../Pages/NotFound";
import Sidebar from "../Components/Sidebar";
import RestrictedAccountScreen from "../Components/RestrictedAccountScreen";
import Explore from "../Pages/Explore";
import Chat from "../Pages/Chat";
import Account from "../Pages/Account";
import RootEntry from "./RootEntry";
import ViewProfile from "../Pages/Profile/ViewProfile";
import CreatorDetails from "../Pages/CreatorDetails";
import Thankyou from "../Pages/Thankyou";
import ResetPassword from "../Pages/ResetPassword";
import ChangePassword from "../Pages/ChangePassword";
import ForgotPassword from "../Pages/ForgotPassword";
import EditProfile from "../Pages/Profile/EditProfile";
import Activity from "../Pages/Activity";
import MembershipPricing from "../Pages/MembershipPricing";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../Redux/store";
import Membership from "../Pages/Membership";
import TermsAndCondition from "../Pages/TermsAndCondition";
import PrivacyPolicy from "../Pages/PrivacyPolicy";
import AboutUs from "../Pages/AboutUs";
import FAQs from "../Pages/Faqs";
import Payouts from "../Pages/Payouts";
import Login from "../Pages/Login";
import SignUp from "../Pages/SignUp";
import LogoLight from "../Assets/Images/FanxoLight.svg";
import FanxoStar from "../Assets/Images/FanxoStar.svg";
import { isAndroid, isIOS } from "react-device-detect";

import PaymentVerification from "../Pages/PaymentVerification/PaymentVerification";
import * as CM from "../Config/common.config";
import { getUserDetails } from "../Redux/slices/userSlice";
import { appendVisitorIdToUrl } from "../Utils/visitorIdentity";
import SafariLegacyShare from "../Assets/install-guide/ios-safari-legacy-share.png";
import Safari26Menu from "../Assets/install-guide/ios-safari-26-menu.png";
import Safari26Share from "../Assets/install-guide/ios-safari-26-share.png";
import Safari27Menu from "../Assets/install-guide/ios-safari-27-menu.png";
import Safari27Share from "../Assets/install-guide/ios-safari-27-share.png";
import Safari27ViewMore from "../Assets/install-guide/ios-safari-27-view-more.png";
import SafariMenuAddHome from "../Assets/install-guide/ios-safari-menu-add-home.png";
import SafariStepFour from "../Assets/install-guide/ios-safari-step-4.png";
import ChromeStepOne from "../Assets/install-guide/ios-chrome-step-1.png";
import ChromeStepTwo from "../Assets/install-guide/ios-chrome-step-2.png";
import ChromeStepThree from "../Assets/install-guide/ios-chrome-step-3.png";

interface RoutesInterface {
  element: React.FC;
  path: string;
  private?: boolean;
  sidebar?: boolean;
  isProfile?: boolean;
  showHeader?: boolean;
  blockPendingCreator?: boolean;
  approvedCreatorOnly?: boolean;
}

interface InstallGuideSlide {
  image: string;
  text: string;
}

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const user = useSelector((state: RootState) => state.user);
  const dispatch: AppDispatch = useDispatch();
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstallGuideOpen, setIsInstallGuideOpen] = useState(false);
  const [isIOSBrowserPromptOpen, setIsIOSBrowserPromptOpen] = useState(false);
  const [installGuideStep, setInstallGuideStep] = useState(0);
  const installGuideTimerRef = useRef<number | null>(null);
  const requestedUserRef = useRef(false);
  const VERSION_KEY = "app_version";
  useEffect(() => {
    const checkVersion = async () => {
      try {
        const res = await fetch("/meta.json?_=" + Date.now()); // prevent cache
        const { version } = await res.json();

        const currentVersion = localStorage.getItem(VERSION_KEY);
        if (currentVersion && currentVersion !== version) {
          localStorage.setItem(VERSION_KEY, version);
          window.location.reload(); // or show update toast
        } else {
          localStorage.setItem(VERSION_KEY, version);
        }
      } catch (err) {
        console.error("Version check failed", err);
      }
    };

    checkVersion();
  }, []);

  const userAgent = navigator.userAgent;
  const isInstagramBrowser = userAgent.includes("Instagram");
  const isStandaloneMode =
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator && Boolean((navigator as Navigator & { standalone?: boolean }).standalone));
  const isIOSChrome = isIOS && /CriOS/i.test(userAgent);
  const isIOSSafari = isIOS && /Safari/i.test(userAgent) && !/CriOS|FxiOS|EdgiOS/i.test(userAgent);
  const iosOsMajorVersion = Number(userAgent.match(/OS (\d+)(?:[._]\d+)?/)?.[1] || 0);
  const safariMajorVersion = Number(userAgent.match(/Version\/(\d+)(?:\.\d+)?/i)?.[1] || 0);
  const iosMajorVersion = isIOSSafari ? Math.max(iosOsMajorVersion, safariMajorVersion) : iosOsMajorVersion;
  const safariInstallSlides: InstallGuideSlide[] =
    iosMajorVersion >= 27
      ? [
          {
            image: Safari27Menu,
            text: "Tap the bottom-left menu icon",
          },
          {
            image: Safari27Share,
            text: "Tap Share",
          },
          {
            image: Safari27ViewMore,
            text: "Tap View More",
          },
          {
            image: SafariMenuAddHome,
            text: "Select Add to Home Screen",
          },
          {
            image: SafariStepFour,
            text: "Tap Add",
          },
        ]
      : iosMajorVersion >= 26
        ? [
            {
              image: Safari26Menu,
              text: "Tap the 3 dots",
            },
            {
              image: Safari26Share,
              text: "Tap Share",
            },
            {
              image: SafariMenuAddHome,
              text: "Select Add to Home Screen",
            },
            {
              image: SafariStepFour,
              text: "Tap Add",
            },
          ]
        : [
            {
              image: SafariLegacyShare,
              text: "Tap the Share icon",
            },
            {
              image: SafariMenuAddHome,
              text: "Select Add to Home Screen",
            },
            {
              image: SafariStepFour,
              text: "Tap Add",
            },
          ];
  const shouldShowIOSBrowserPrompt = isIOS && !isStandaloneMode && !isIOSSafari && !isIOSChrome;
  const shouldShowIOSInstallGuide = isIOS && !isStandaloneMode && (isIOSSafari || isIOSChrome);
  const shouldShowNativeInstallPrompt = isAndroid && !isInstagramBrowser && !isStandaloneMode && Boolean(deferredPrompt);
  const installGuideSlides: InstallGuideSlide[] = isIOSChrome
    ? [
        {
          image: ChromeStepOne,
          text: "Tap the Share icon",
        },
        {
          image: ChromeStepTwo,
          text: "Select Add to Home Screen",
        },
        {
          image: ChromeStepThree,
          text: "Tap Add",
        },
      ]
    : safariInstallSlides;

  const clearInstallGuideTimer = () => {
    if (installGuideTimerRef.current !== null) {
      window.clearInterval(installGuideTimerRef.current);
      installGuideTimerRef.current = null;
    }
  };

  const openInstallGuide = () => {
    clearInstallGuideTimer();
    setInstallGuideStep(0);
    setIsInstallGuideOpen(true);
  };

  const closeInstallGuide = () => {
    clearInstallGuideTimer();
    setIsInstallGuideOpen(false);
  };

  const closeIOSBrowserPrompt = () => {
    setIsIOSBrowserPromptOpen(false);
  };

  const openSafariFallbackPrompt = () => {
    closeInstallGuide();
    setIsIOSBrowserPromptOpen(true);
  };

  const handleNativeInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        console.log("User accepted the install prompt");
      } else {
        console.log("User dismissed the install prompt");
      }
      setDeferredPrompt(null);
    }
  };

  const handleInstallClick = () => {
    if (shouldShowIOSBrowserPrompt) {
      setIsIOSBrowserPromptOpen(true);
      return;
    }

    if (shouldShowIOSInstallGuide) {
      openInstallGuide();
      return;
    }

    if (shouldShowNativeInstallPrompt) {
      handleNativeInstall();
    }
  };

  const renderInstallAction = () => {
    if (!shouldShowIOSBrowserPrompt && !shouldShowIOSInstallGuide && !shouldShowNativeInstallPrompt) return null;

    return (
      <R.InstallLink onClick={handleInstallClick} title="Install App" role="button" tabIndex={0}>
        Install App
      </R.InstallLink>
    );
  };

  const renderMobileHeaderBrand = () => (
    <R.HeaderBrand aria-label="Fanxo">
      <R.BrandStar src={FanxoStar} alt="" aria-hidden="true" />
      <R.BrandWordmark src={LogoLight} alt="Fanxo" />
    </R.HeaderBrand>
  );
  const routes: RoutesInterface[] = [
    // Public Routes
    { element: RootEntry, path: "/", sidebar: false, showHeader: false },
    { element: Login, path: "/login", sidebar: false, showHeader: false },
    { element: SignUp, path: "/signup", sidebar: false, showHeader: false },
    {
      element: SignUp,
      path: "/signup/:role",
      sidebar: false,
      showHeader: false,
    },
    {
      element: ViewProfile,
      path: "/profile/:username",
      sidebar: false,
      showHeader: true,
      isProfile: true,
    },
    {
      element: ViewProfile,
      path: "/:username",
      sidebar: false,
      showHeader: true,
      isProfile: true,
    },
    {
      element: CreatorDetails,
      path: "/creator-details",
      sidebar: false,
      showHeader: false,
    },
    {
      element: Thankyou,
      path: "/thankyou-for-applying",
      sidebar: false,
      showHeader: false,
    },
    {
      element: ResetPassword,
      path: "/password/reset/:token",
      sidebar: false,
      showHeader: false,
    },
    {
      element: ForgotPassword,
      path: "/password/forgot",
      sidebar: false,
      showHeader: false,
    },
    {
      element: TermsAndCondition,
      path: "/terms-and-conditions",
      sidebar: false,
      showHeader: false,
    },
    {
      element: PrivacyPolicy,
      path: "/privacy-policy",
      sidebar: false,
      showHeader: false,
    },
    { element: AboutUs, path: "/about-us", sidebar: false, showHeader: false },
    { element: FAQs, path: "/faqs", sidebar: false, showHeader: false },

    // Private Routes
    {
      element: Home,
      path: "/home",
      sidebar: true,
      private: true,
      showHeader: true,
      blockPendingCreator: true,
    },
    {
      element: Explore,
      path: "/explore",
      sidebar: true,
      private: true,
      showHeader: false,
    },
    {
      element: Chat,
      path: "/messages",
      sidebar: true,
      private: true,
      showHeader: false,
    },
    {
      element: Account,
      path: "/account",
      sidebar: true,
      private: true,
      showHeader: false,
    },
    {
      element: ChangePassword,
      path: "/password/change",
      sidebar: true,
      private: true,
      showHeader: false,
    },
    {
      element: EditProfile,
      path: "/profile/edit",
      sidebar: true,
      private: true,
      showHeader: false,
      blockPendingCreator: true,
    },
    {
      element: Activity,
      path: "/activity",
      sidebar: true,
      private: true,
      showHeader: false,
      approvedCreatorOnly: true,
    },
    {
      element: MembershipPricing,
      path: "/membership/pricing",
      sidebar: true,
      private: true,
      showHeader: false,
      approvedCreatorOnly: true,
    },
    {
      element: Membership,
      path: "/user-membership",
      sidebar: true,
      private: true,
      showHeader: false,
    },
    {
      element: Payouts,
      path: "/request-payouts",
      sidebar: true,
      private: true,
      showHeader: false,
      approvedCreatorOnly: true,
    },
    {
      element: PaymentVerification,
      path: "/payment-verification",
      sidebar: false,
      showHeader: false,
    },
    // Not Found
    { element: NotFound, path: "*" },
  ];

  const componentRenderHandler = ({
    element: Element,
    sidebar,
    private: checkAuth,
    isProfile,
    showHeader,
    blockPendingCreator,
    approvedCreatorOnly,
  }: RoutesInterface) => {
    // Handle /profile/:username route separately
    if (isProfile) {
      return (
        <ProfileWrapper>
          {
            <>
              {showHeader && (
                <R.Header>
                  {renderMobileHeaderBrand()}
                  {navigator.userAgent.includes("Instagram") && isAndroid && (
                    <R.OpenLinks
                      onClick={() => {
                        const handoffUrl = appendVisitorIdToUrl(window.location.href);
                        window.location.href =
                          "intent://" +
                          handoffUrl.replace(/^https?:\/\//, "") +
                          "#Intent;scheme=https;package=com.android.chrome;end";
                      }}
                    >
                      {"Open in chrome"}
                    </R.OpenLinks>
                  )}
                  {renderInstallAction()}
                </R.Header>
              )}
              <Element />
            </>
          }
        </ProfileWrapper>
      );
    }

    const PathElement = sidebar ? (
      <R.RoutesWrapper>
        {sidebar && isAuthenticated && <Sidebar />}
        <R.Content>
          {showHeader && (
            <R.Header>
              {renderMobileHeaderBrand()}
              {navigator.userAgent.includes("Instagram") && isAndroid && (
                <R.OpenLinks
                  onClick={() => {
                    const handoffUrl = appendVisitorIdToUrl(window.location.href);
                    window.location.href =
                      "intent://" +
                      handoffUrl.replace(/^https?:\/\//, "") +
                      "#Intent;scheme=https;package=com.android.chrome;end";
                  }}
                >
                  {"Open in chrome"}
                </R.OpenLinks>
              )}
              {renderInstallAction()}
            </R.Header>
          )}
          <Element />
        </R.Content>
      </R.RoutesWrapper>
    ) : (
      <Element />
    );

    const isCreator = CM.isCreatorRole(user.role);
    const approvedCreator = isCreator && user.isApproved === true;

    // Only restrict private routes
    if (checkAuth && !isAuthenticated) return <Navigate to="/login" replace />;
    if (
      checkAuth &&
      isAuthenticated &&
      (blockPendingCreator || approvedCreatorOnly) &&
      !user.id
    )
      return null;
    if ((blockPendingCreator || approvedCreatorOnly) && isCreator && !approvedCreator)
      return <Navigate to="/thankyou-for-applying" replace />;
    if (approvedCreatorOnly && user.id && !approvedCreator)
      return <Navigate to="/home" replace />;
    return PathElement;
  };

  useEffect(() => {
    if (!isAuthenticated) {
      requestedUserRef.current = false;
      return;
    }

    if (!user.id && !user.loading && !requestedUserRef.current) {
      requestedUserRef.current = true;
      dispatch(getUserDetails());
    }
  }, [dispatch, isAuthenticated, user.id, user.loading]);

  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handler);

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    clearInstallGuideTimer();

    if (!isInstallGuideOpen || installGuideSlides.length <= 1 || installGuideStep >= installGuideSlides.length - 1) return;

    installGuideTimerRef.current = window.setInterval(() => {
      setInstallGuideStep((currentStep) => {
        if (currentStep >= installGuideSlides.length - 1) {
          clearInstallGuideTimer();
          return currentStep;
        }

        return currentStep + 1;
      });
    }, 3800);

    return clearInstallGuideTimer;
  }, [installGuideSlides.length, installGuideStep, isInstallGuideOpen]);

  const activeInstallSlide = installGuideSlides[installGuideStep];
  const isFinalInstallSlide = installGuideStep === installGuideSlides.length - 1;

  const shouldShowRestrictedAccount = isAuthenticated && Boolean(user.id) && user.isDeleted === true;

  return (
    <Router>
      {shouldShowRestrictedAccount ? (
        <RestrictedAccountScreen />
      ) : (
        <Routes>
        {routes.map((route) => (
          <Route
            key={`key-${route.path}`}
            element={<>{componentRenderHandler(route)}</>}
            path={route.path}
          />
        ))}
        </Routes>
      )}
      {isIOSBrowserPromptOpen && (
        <R.InstallGuideOverlay role="dialog" aria-modal="true" aria-label="Open Fanxo in Safari">
          <R.InstallGuidePanel>
            <R.InstallGuideClose type="button" aria-label="Close Safari prompt" onClick={closeIOSBrowserPrompt}>
              X
            </R.InstallGuideClose>
            <R.InstallGuideLogo>fanxo</R.InstallGuideLogo>
            <R.InstallGuideTitle>Open in Safari</R.InstallGuideTitle>
            <R.InstallGuideBrowserCard>
              <R.InstallGuideText>
                Open Fanxo in Safari, then tap Install App to add it to your Home Screen.
              </R.InstallGuideText>
              <R.InstallGuideButton type="button" onClick={closeIOSBrowserPrompt}>
                Got it
              </R.InstallGuideButton>
            </R.InstallGuideBrowserCard>
          </R.InstallGuidePanel>
        </R.InstallGuideOverlay>
      )}
      {isInstallGuideOpen && activeInstallSlide && (
        <R.InstallGuideOverlay role="dialog" aria-modal="true" aria-label="Install Fanxo app guide">
          <R.InstallGuidePanel>
            <R.InstallGuideClose type="button" aria-label="Close install guide" onClick={closeInstallGuide}>
              X
            </R.InstallGuideClose>
            <R.InstallGuideLogo>fanxo</R.InstallGuideLogo>
            <R.InstallGuideTitle>Add to Home Screen</R.InstallGuideTitle>
            <R.InstallGuideCard>
              <R.InstallGuideImageWrap>
                {activeInstallSlide.image ? (
                  <R.InstallGuideImageFrame>
                    <R.InstallGuideImage src={activeInstallSlide.image} alt={activeInstallSlide.text} />
                    {isIOSChrome && installGuideStep === 0 && (
                      <R.InstallGuideShareHighlight aria-hidden="true" />
                    )}
                  </R.InstallGuideImageFrame>
                ) : (
                  <R.InstallGuidePlaceholder>
                    <span>Open the browser menu</span>
                    <strong>Add to Home Screen</strong>
                  </R.InstallGuidePlaceholder>
                )}
              </R.InstallGuideImageWrap>
              <R.InstallGuideText>{activeInstallSlide.text}</R.InstallGuideText>
              {isIOSChrome && installGuideStep === 1 && (
                <R.InstallGuideFallback>
                  <span>If Add to Home Screen is not available, continue in Safari.</span>
                  <button type="button" onClick={openSafariFallbackPrompt}>
                    Open in Safari <span aria-hidden="true">{"\u2192"}</span>
                  </button>
                </R.InstallGuideFallback>
              )}
              <R.InstallGuideDots>
                {installGuideSlides.map((slide, index) => (
                  <R.InstallGuideDot
                    key={slide.text}
                    type="button"
                    aria-label={`Go to step ${index + 1}`}
                    $active={index === installGuideStep}
                    onClick={() => setInstallGuideStep(index)}
                  />
                ))}
              </R.InstallGuideDots>
              {isFinalInstallSlide ? (
                <R.InstallGuideActions>
                  <R.InstallGuideSecondaryButton type="button" onClick={() => setInstallGuideStep(0)}>
                    See Again
                  </R.InstallGuideSecondaryButton>
                  <R.InstallGuideButton type="button" onClick={closeInstallGuide}>
                    Got it
                  </R.InstallGuideButton>
                </R.InstallGuideActions>
              ) : (
                <R.InstallGuideButton
                  type="button"
                  onClick={() => {
                    setInstallGuideStep((currentStep) => Math.min(currentStep + 1, installGuideSlides.length - 1));
                  }}
                >
                  Next
                </R.InstallGuideButton>
              )}
            </R.InstallGuideCard>
          </R.InstallGuidePanel>
        </R.InstallGuideOverlay>
      )}
    </Router>
  );
};

export default AppRoutes;

// Profile Wrapper to conditionally show sidebar
const ProfileWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  return isAuthenticated ? (
    <R.RoutesWrapper>
      <Sidebar />
      <R.Content>{children}</R.Content>
    </R.RoutesWrapper>
  ) : (
    <>{children}</>
  );
};
